// Tax year 2026 constants. Every value cites its source; update only from a newer official source.
// Sources checked 2026-10-17:
//  RP  = Rev. Proc. 2025-32 (https://www.irs.gov/pub/irs-drop/rp-25-32.pdf), reflects OBBBA
//  SSA = https://www.ssa.gov/news/en/cola/factsheets/2026.html
//  ES  = 2026 Form 1040-ES, rev. Feb 12 2026 (final) (https://www.irs.gov/pub/irs-pdf/f1040es.pdf)
//  T560 = IRS Topic 560 (Additional Medicare Tax); NIIT = IRC §1411 (thresholds not indexed)

export type FilingStatus = "single" | "mfj" | "mfs" | "hoh" | "qss";

export interface Bracket {
  /** Taxable income at which this rate starts. */
  from: string;
  rate: string;
}

const singleBrackets: Bracket[] = [
  { from: "0", rate: "0.10" },
  { from: "12400", rate: "0.12" },
  { from: "50400", rate: "0.22" },
  { from: "105700", rate: "0.24" },
  { from: "201775", rate: "0.32" },
  { from: "256225", rate: "0.35" },
  { from: "640600", rate: "0.37" },
];
const mfjBrackets: Bracket[] = [
  { from: "0", rate: "0.10" },
  { from: "24800", rate: "0.12" },
  { from: "100800", rate: "0.22" },
  { from: "211400", rate: "0.24" },
  { from: "403550", rate: "0.32" },
  { from: "512450", rate: "0.35" },
  { from: "768700", rate: "0.37" },
];
const hohBrackets: Bracket[] = [
  { from: "0", rate: "0.10" },
  { from: "17700", rate: "0.12" },
  { from: "67450", rate: "0.22" },
  { from: "105700", rate: "0.24" },
  { from: "201750", rate: "0.32" },
  { from: "256200", rate: "0.35" },
  { from: "640600", rate: "0.37" },
];
const mfsBrackets: Bracket[] = [
  { from: "0", rate: "0.10" },
  { from: "12400", rate: "0.12" },
  { from: "50400", rate: "0.22" },
  { from: "105700", rate: "0.24" },
  { from: "201775", rate: "0.32" },
  { from: "256225", rate: "0.35" },
  { from: "384350", rate: "0.37" },
];

export const RULES_2026 = {
  taxYear: 2026,
  sources: {
    brackets: "Rev. Proc. 2025-32 §4.01",
    capitalGains: "Rev. Proc. 2025-32 §4.03",
    standardDeduction: "Rev. Proc. 2025-32 §4.14",
    qbi: "Rev. Proc. 2025-32 §4.26",
    ssWageBase: "SSA 2026 COLA fact sheet",
    estimatedTax: "2026 Form 1040-ES (final, rev. Feb 12 2026)",
    additionalMedicare: "IRS Topic 560; IRC §3101(b)(2), §1401(b)(2)",
    niit: "IRC §1411; Form 8960 instructions",
    section1256: "IRC §1256(a)(3); Form 6781 lines 8–9",
    seTax: "IRC §1401, §1402(a)(12); Schedule SE instructions",
  },
  brackets: {
    single: singleBrackets,
    mfj: mfjBrackets,
    qss: mfjBrackets,
    hoh: hohBrackets,
    mfs: mfsBrackets,
  } as Record<FilingStatus, Bracket[]>,
  /** Top of the 0% and 15% long-term capital gain bands (taxable income). */
  capitalGains: {
    single: { zeroTop: "49450", fifteenTop: "545500" },
    mfj: { zeroTop: "98900", fifteenTop: "613700" },
    qss: { zeroTop: "98900", fifteenTop: "613700" },
    hoh: { zeroTop: "66200", fifteenTop: "579600" },
    mfs: { zeroTop: "49450", fifteenTop: "306850" },
  } as Record<FilingStatus, { zeroTop: string; fifteenTop: string }>,
  standardDeduction: {
    single: "16100",
    mfs: "16100",
    mfj: "32200",
    qss: "32200",
    hoh: "24150",
  } as Record<FilingStatus, string>,
  section1256: { shortTermShare: "0.40", longTermShare: "0.60" },
  seTax: {
    netEarningsFactor: "0.9235",
    socialSecurityRate: "0.124",
    medicareRate: "0.029",
    socialSecurityWageBase: "184500",
    minimumNetEarnings: "400",
  },
  additionalMedicare: {
    rate: "0.009",
    threshold: { single: "200000", hoh: "200000", qss: "200000", mfj: "250000", mfs: "125000" } as Record<FilingStatus, string>,
  },
  niit: {
    rate: "0.038",
    threshold: { single: "200000", hoh: "200000", qss: "250000", mfj: "250000", mfs: "125000" } as Record<FilingStatus, string>,
  },
  qbi: {
    threshold: { single: "201750", hoh: "201750", qss: "201750", mfj: "403500", mfs: "201775" } as Record<FilingStatus, string>,
    phaseInEnd: { single: "276750", hoh: "276750", qss: "276750", mfj: "553500", mfs: "276775" } as Record<FilingStatus, string>,
    /** OBBBA minimum deduction, first year 2026 (indexed after 2026): IRC §199A(i). */
    minimumDeduction: "400",
    minimumActiveQbi: "1000",
  },
  estimatedTax: {
    minimumBalanceDue: "1000",
    currentYearShare: "0.90",
    priorYearShare: "1.00",
    priorYearShareHighIncome: "1.10",
    /** Prior-year AGI above which the 110% rule applies ($75,000 if MFS for the current year). */
    highIncomeAgi: "150000",
    highIncomeAgiMfs: "75000",
    dueDates: ["2026-04-15", "2026-06-15", "2026-09-15", "2027-01-15"],
    /** ES: the January 15, 2027 payment isn't needed if the 2026 return is filed by this date with the full balance paid. */
    fileByToSkipFinalPayment: "2027-02-01",
  },
  informationReturns: {
    /** 1099-NEC / 1099-MISC (boxes 1 and 3) threshold for payments made after 2025 (OBBBA §70433). */
    necThreshold: "2000",
  },
} as const;

export type Rules = typeof RULES_2026;
